import { useAuth0 } from '@auth0/auth0-react'
import { cn } from '@dfh-corp-sites/ui/utils'
import { useLocation } from '@gatsbyjs/reach-router'
import { Subscribe } from '@react-rxjs/core'
import React, { FC, useCallback } from 'react'

import { SanityParentProductFragment } from '../../../../graphql/gatsby'
import {
  ConfigurableProductDetailsFragment,
  SimpleProductDetailsFragment,
} from '../../../../graphql/magento'
import { Maybe, Money } from '../../../../graphql/search'
import useRetailPrices from '../../../../hooks/useRetailPrices'
import useSiteMetadata from '../../../../hooks/useSiteMetadata'
import { useTestKitPrice } from '../../../../hooks/useTestKitPrice'
import { useDispensaryContext } from '../../../../lib/dispensaries'
import { usePayUpfrontOption } from '../../../../lib/products/hooks/usePayUpfrontOption'
import { useTestKitDelayedBilling } from '../../../../lib/products/hooks/useTestKitDelayedBilling'
import {
  findVariantInProductQuery,
  useQueryProductByUrlKeyVaryByAuth,
} from '../../../../lib/products/m2-products-by-url-key'
import { imageUrlFor } from '../../../../utils/image-url'
import { displayAttribute } from '../../../../utils/itemTools'
import { formatAsCurrency, itemPrice } from '../../../../utils/priceTools'
import { StyledFunctionComponent } from '../../../types'
import { TypographyShimmer } from '../../../TypographyShimmer'
import * as Styled from './styled'

export interface ItemHeadingProps {
  className?: string
  item: ConfigurableProductDetailsFragment
  sanityParentProduct?: SanityParentProductFragment | null
  selectedVariant: SimpleProductDetailsFragment | null | undefined
  retailPrice: Maybe<Money> | undefined
}

/**
 * Display variant price when authentication state settled
 */
export const VariantPriceReactive: StyledFunctionComponent<{
  item: ConfigurableProductDetailsFragment
  selectedVariant: SimpleProductDetailsFragment | null | undefined
  retailPrice: Maybe<Money> | undefined
}> = ({ item, selectedVariant, retailPrice }) => {
  const location = useLocation()
  const getCurrentPath = useCallback(() => `${location.pathname}${location.search}`, [location])
  const isEScriptPage = getCurrentPath().includes('/escript/build')
  const payUpfrontOption = usePayUpfrontOption(selectedVariant)
  const testKitDelayedBilling = useTestKitDelayedBilling(selectedVariant)
  const { shouldDisplayRetailPrice } = useRetailPrices()
  const result = useQueryProductByUrlKeyVaryByAuth(item.url_key ?? '')
  const itemId = item.id
  const sku = selectedVariant?.sku

  const variantForAuth =
    itemId == null || !sku || result._tag === 'Left'
      ? undefined
      : findVariantInProductQuery(result.data, { itemId, sku })

  const retailPriceAmount =
    retailPrice?.amount && shouldDisplayRetailPrice
      ? formatAsCurrency({ amount: parseFloat(retailPrice.amount) })
      : undefined
  const price = variantForAuth
    ? itemPrice({
        item: variantForAuth.product,
        ignoreFalsyAmount: !(testKitDelayedBilling && !!retailPriceAmount),
      })
    : undefined
  // Do not display price if it's null or if both the pay upfront option is available
  // and the current path is not for the eScript builder
  return price == null || (!!payUpfrontOption && !isEScriptPage) ? null : (
    <>
      <div className="flex items-center text-lg md:text-xl xl:text-2xl">
        {isEScriptPage && retailPriceAmount ? (
          <span>{retailPriceAmount} retail</span>
        ) : (
          <>
            <span className="font-semibold">{price}</span>
            {!testKitDelayedBilling && retailPriceAmount && (
              <span className='before:content-["|"] before:mx-1'>{retailPriceAmount} retail</span>
            )}
          </>
        )}
      </div>
      {/*TODO: refactor styling for test kit delayed billing components*/}
      {testKitDelayedBilling && retailPriceAmount && !isEScriptPage && (
        <Styled.DelayedBilling>
          <Styled.DelayedBillingPrice>{retailPriceAmount}</Styled.DelayedBillingPrice>
          <Styled.DelayedBillingMessage>
            {' '}
            to be charged when a sample is processed by Diagnostic Solutions Lab
          </Styled.DelayedBillingMessage>{' '}
        </Styled.DelayedBilling>
      )}
    </>
  )
}

/**
 * Loading component for variant price
 */
export const VariantPriceSkeleton: FC = () => (
  <div className="flex items-center">
    <Styled.Price>
      <TypographyShimmer $width="6em" />
    </Styled.Price>
  </div>
)

const ItemHeading: FC<ItemHeadingProps> = ({
  className,
  item,
  sanityParentProduct,
  selectedVariant = null,
  retailPrice,
}) => {
  const npn = displayAttribute({ item, selectedVariant, attribute: 'natural_product_number' })
  const headerImage = sanityParentProduct?.headerImage?.asset
    ? imageUrlFor(sanityParentProduct.headerImage).url()
    : undefined
  const { featureFlags } = useSiteMetadata() ?? {}
  const { shouldShowTestKitPrice } = useTestKitPrice()
  const { isAuthenticated } = useAuth0()
  const dispensary = useDispensaryContext()
  const anonymousCart = featureFlags?.anonymousCart ?? false
  const showPrice = isAuthenticated || !!dispensary || anonymousCart
  const isTestKitPrice = shouldShowTestKitPrice(sanityParentProduct?.sku ?? '')

  return (
    <header className={cn('text-center flex flex-col items-center gap-3 xl:gap-6', className)}>
      {headerImage && <Styled.HeaderImage src={headerImage} />}
      <span className="text-xs text-brand-navy py-1 px-3 xl:px-3.5 uppercase border border-brand-navy rounded-[80px]">
        SKU: {displayAttribute({ item, selectedVariant, attribute: 'sku' })?.toUpperCase()}
        {npn && <span>{`NPN: ${npn}`}</span>}
      </span>
      <h1 className="text-3xl text-5lx font-bold text-brand-navy">{item.name}</h1>
      {showPrice && isTestKitPrice && (
        <Subscribe fallback={<VariantPriceSkeleton />}>
          <VariantPriceReactive
            item={item}
            selectedVariant={selectedVariant}
            retailPrice={retailPrice}
          />
        </Subscribe>
      )}
      {item.variants?.length === 1 && (
        <h2>
          {item.variants[0]?.attributes
            ?.map((attribute) => attribute?.label ?? '')
            .filter(Boolean)
            .join(' ')}
        </h2>
      )}
    </header>
  )
}

export default ItemHeading
